import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { getPracticeStatus } from '../api/practiceStatusAPI';
import { PracticeStatus, PracticeEntitlements } from '../types/practiceStatus';
import { UserRole } from '../types/user';
import { logApiError } from '../utils/errorUtils';
import { hasNoPractice } from '../utils/userUtils';

import { useAuth } from './AuthContext';

// Roles that never own/manage a practice — the underlying endpoints
// (/api/practice/status, /UserPracticeProfile) are PracticeAdmin-scoped and
// 403 for anyone else (HA-382 extended this from Client-only to also cover
// Practitioner). A SystemAdmin belongs to no practice at all (HA-446), so it is
// skipped too: waiting on its status held the admin console behind a loader.
// Shared by the fetch-skip branch and the loading-state calculation below so
// the two can't drift apart.
const isNonOwnerRole = (role: UserRole): boolean =>
  role === UserRole.Client || role === UserRole.Practitioner || hasNoPractice(role);

interface PracticeContextType {
  /** The full server-computed practice status. Null until first fetch completes. */
  status: PracticeStatus | null;
  /** True while the initial fetch is in flight. */
  isLoading: boolean;
  /** Shortcut: status?.isActive ?? false. Pessimistic default prevents false access. */
  isActive: boolean;
  /** Shortcut: status?.hasCompletedOnboarding ?? false */
  hasCompletedOnboarding: boolean;
  /** Shortcut: status?.isTrialExpired ?? false */
  isTrialExpired: boolean;
  /** Shortcut: status?.trialDaysRemaining ?? 0 */
  daysRemaining: number;
  /** Shortcut: status?.tier ?? 'trial' */
  tier: string;
  /** Shortcut: status?.entitlements ?? null */
  entitlements: PracticeEntitlements | null;
  /** Re-fetches practice status from the server. Call after checkout or onboarding. */
  refresh: () => Promise<void>;
}

export const PracticeContext = createContext<PracticeContextType | undefined>(undefined);

interface PracticeProviderProps {
  children: ReactNode;
}

export const PracticeProvider: React.FC<PracticeProviderProps> = ({ children }) => {
  const { currentUser, isAuthenticated: authed, isLoading: authLoading } = useAuth();
  const [status, setStatus] = useState<PracticeStatus | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  // Tracks whether we have completed at least one fetch attempt for the
  // current auth session. Reset when the user logs out so the next login
  // correctly shows a loading state until the fetch finishes.
  const [, setHasFetched] = useState<boolean>(false);

  const fetchStatus = useCallback(async () => {
    if (!authed || !currentUser) {
      // Not authenticated — reset to defaults so stale data never lingers.
      setStatus(null);
      setHasFetched(false);
      setIsLoading(false);

      return;
    }

    // Non-owner roles (Client, Practitioner) 403 against the PracticeAdmin-
    // scoped endpoints and pollute the console on every page load (HA-382).
    // Skip the fetch and surface neutral defaults instead.
    if (isNonOwnerRole(currentUser.role)) {
      setStatus(null);
      setHasFetched(true);
      setIsLoading(false);

      return;
    }

    try {
      setIsLoading(true);
      const data = await getPracticeStatus();

      setStatus(data);
    } catch (error) {
      logApiError('PracticeContext.fetchStatus', error);
      // On error, leave status null — isActive stays false (pessimistic)
    } finally {
      setHasFetched(true);
      setIsLoading(false);
    }
  }, [authed, currentUser]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Treat the context as "loading" when:
  // 1. AuthContext is still determining auth state (authLoading), OR
  // 2. Our own fetch is in flight (isLoading), OR
  // 3. An owner-role user (PracticeAdmin) is authenticated but
  //    status hasn't been resolved yet
  // Using status === null instead of !hasFetched ensures we stay in loading
  // until we have an actual API response. This prevents Dashboard from seeing
  // hasCompletedOnboarding=false (pessimistic default) and redirecting to
  // onboarding before the real data arrives.
  // Non-owner roles (Client, Practitioner — HA-382; SystemAdmin — HA-446) never fetch, so they
  // exit loading immediately once auth resolves: status stays null forever
  // for them by design, not a pending fetch. Using isNonOwnerRole (the same
  // check the skip branch uses) instead of hardcoding a single role keeps
  // this correct for every non-owner role as the list changes.
  const effectiveIsLoading =
    authLoading ||
    isLoading ||
    (authed && currentUser != null && !isNonOwnerRole(currentUser.role) && status === null);

  const contextValue = useMemo<PracticeContextType>(
    () => ({
      status,
      isLoading: effectiveIsLoading,
      isActive: status?.isActive ?? false,
      hasCompletedOnboarding: status?.hasCompletedOnboarding ?? false,
      isTrialExpired: status?.isTrialExpired ?? false,
      daysRemaining: status?.trialDaysRemaining ?? 0,
      tier: status?.tier ?? 'trial',
      entitlements: status?.entitlements ?? null,
      refresh: fetchStatus,
    }),
    [status, effectiveIsLoading, fetchStatus]
  );

  return <PracticeContext.Provider value={contextValue}>{children}</PracticeContext.Provider>;
};

export const usePractice = (): PracticeContextType => {
  const context = useContext(PracticeContext);

  if (context === undefined) {
    throw new Error('usePractice must be used within a PracticeProvider');
  }

  return context;
};
